/**
 * Traducción de un `ApiError` a algo que se le puede mostrar a una persona.
 *
 * **Se mapea el `code`, nunca el `message` del servidor y nunca el status.**
 * El `message` de la API está en español y es correcto, pero es texto de
 * backend: menciona tokens, roles y tablas. Sirve como respaldo cuando el
 * `code` es uno que este dashboard todavía no conoce —cosa que va a pasar,
 * porque hay tres agentes agregando endpoints— y ahí es mejor un mensaje del
 * servidor que un "error desconocido".
 *
 * Cómo agregar un código nuevo desde un módulo:
 *   1. agregá las claves al catálogo (`messages.ts`),
 *   2. agregá la entrada a `ERROR_MESSAGES`.
 * Si no lo hacés, el error igual se muestra: cae en el respaldo.
 */
import type { ApiError } from '@/api/errors';
import type { Translate } from './translate';
import type { MessageKey } from './messages';

export interface ErrorPresentation {
  title: string;
  /** Qué puede hacer la persona. Vacío si no hay nada útil que decir. */
  hint: string | null;
  /** `X-Trace-Id`. Se muestra en chico; es lo que pide soporte. */
  traceId: string | null;
  /** ¿Tiene sentido ofrecer "reintentar"? */
  retriable: boolean;
}

const ERROR_MESSAGES: Record<string, { title: MessageKey; hint?: MessageKey }> = {
  network_error: { title: 'error.network', hint: 'error.network.hint' },
  timeout: { title: 'error.timeout', hint: 'error.timeout.hint' },
  no_session: { title: 'auth.session.expired' },
  invalid_credentials: { title: 'error.invalidCredentials' },
  unauthorized: { title: 'error.unauthorized' },
  invalid_token: { title: 'error.unauthorized' },
  token_expired: { title: 'error.unauthorized' },
  invalid_refresh_token: { title: 'auth.session.expired' },
  refresh_token_reused: { title: 'auth.session.reused' },
  forbidden: { title: 'error.forbidden' },
  invalid_current_password: { title: 'account.password.wrongCurrent' },
  user_is_federated: { title: 'account.password.federated' },
  insufficient_role: { title: 'error.forbidden' },
  // #143: una marca que intenta comprar o generar etiquetas. No es un
  // problema de rol —es admin— sino de quién hace qué; la pista dice por
  // dónde va su camino.
  backoffice_only: { title: 'error.backofficeOnly', hint: 'error.backofficeOnly.hint' },
  not_found: { title: 'error.notFound' },
  conflict: { title: 'error.conflict' },
  validation_error: { title: 'error.validation' },
  invalid_cursor: { title: 'error.invalidCursor' },
  rate_limited: { title: 'error.rateLimited' },
  internal_error: { title: 'error.internal', hint: 'error.internal.hint' },
  malformed_response: { title: 'error.internal', hint: 'error.internal.hint' },
};

export function describeApiError(error: ApiError, t: Translate): ErrorPresentation {
  const mapped = ERROR_MESSAGES[error.code];
  return {
    title: mapped === undefined ? error.message || t('error.unknown') : t(mapped.title),
    hint: mapped?.hint === undefined ? null : t(mapped.hint),
    traceId: error.traceId,
    retriable: error.isRetriable,
  };
}
